import { createApi } from "@reduxjs/toolkit/query/react";

import { type IDistrictSchoolsListResponse } from "@/providers/store/api/districts/districts.interface";
import {
  type ICreateEdlinkIntegrationRequest,
  type ICreateEdlinkOnboardingRequest,
  type ICustomFieldMappingChange,
  type IEdlinkIntegration,
  type IEdlinkOnboarding,
  type IEdlinkSettings,
  type IEdlinkSyncJob,
  type IEdlinkSyncJobErrorsListResponse,
  type IEnqueueCustomFieldBackfillResponse,
  type IEnqueueEdlinkSyncRequest,
  type IEnqueueEdlinkSyncResponse,
  type IListCustomFieldMappingsResponse,
  type IRefreshCustomPropertyKeysResponse,
  type ISuggestCustomFieldMappingsResponse,
  type IUpdateEdlinkIntegrationRequest,
  type IUpdateEdlinkSettingsRequest,
  type IUpdateEdlinkSettingsResponse,
} from "@/providers/store/api/edlink/edlink.interface";
import { safeInvalidateTags } from "@/providers/store/api/safe-invalidate-tags";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase, { type ICustomFetchBaseExtraOptions } from "../custom-fetch-base";

// Onboarding errors are surfaced as toasts on the integrations page, not as the global 500 screen
const onboardingExtraOptions: ICustomFetchBaseExtraOptions = { skipServerErrorRedirect: true };

const edlinkApi = createApi({
  reducerPath: "edlinkApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.EdlinkIntegrations, ApiTags.EdlinkSettings, ApiTags.EdlinkCustomFieldMappings],
  endpoints: (builder) => ({
    listIntegrations: builder.query<IEdlinkIntegration[], { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/edlink/integrations`,
        method: "GET",
      }),
      providesTags: [ApiTags.EdlinkIntegrations],
      transformResponse: (result: IApiResponse<IEdlinkIntegration[]>) => result.data ?? [],
    }),
    createIntegration: builder.mutation<
      IEdlinkIntegration,
      ICreateEdlinkIntegrationRequest & { districtId: string }
    >({
      query: ({
        districtId,
        accessToken,
        schoolIds,
        edlinkIntegrationId,
      }) => ({
        url: `/districts/${districtId}/edlink/integrations`,
        method: "POST",
        body: {
          accessToken,
          schoolIds,
          edlinkIntegrationId,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations]),
      transformResponse: (result: IApiResponse<IEdlinkIntegration>) => result.data,
    }),
    updateIntegration: builder.mutation<
      IEdlinkIntegration,
      IUpdateEdlinkIntegrationRequest & { districtId: string; id: string }
    >({
      query: ({
        districtId,
        id,
        accessToken,
        schoolIds,
        status,
      }) => ({
        url: `/districts/${districtId}/edlink/integrations/${id}`,
        method: "PATCH",
        body: {
          accessToken,
          schoolIds,
          status,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations]),
      transformResponse: (result: IApiResponse<IEdlinkIntegration>) => result.data,
    }),
    listIntegrationSchoolOptions: builder.query<
      IDistrictSchoolsListResponse,
      { districtId: string; integrationId: string }
    >({
      query: ({ districtId, integrationId }) => ({
        url: `/districts/${districtId}/edlink/integrations/${integrationId}/school-options`,
        method: "GET",
      }),
      transformResponse: (result: IApiResponse<IDistrictSchoolsListResponse>) => result.data,
    }),
    deleteIntegration: builder.mutation<void, { districtId: string; id: string }>({
      query: ({ districtId, id }) => ({
        url: `/districts/${districtId}/edlink/integrations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations]),
    }),
    startOnboarding: builder.mutation<
      IEdlinkOnboarding,
      ICreateEdlinkOnboardingRequest & { districtId: string }
    >({
      extraOptions: onboardingExtraOptions,
      query: ({ districtId, schoolIds }) => ({
        url: `/districts/${districtId}/edlink/onboardings`,
        method: "POST",
        body: { schoolIds },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations]),
      transformResponse: (result: IApiResponse<IEdlinkOnboarding>) => result.data,
    }),
    getPendingOnboarding: builder.query<IEdlinkOnboarding | null, { districtId: string }>({
      extraOptions: onboardingExtraOptions,
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/edlink/onboardings/pending`,
        method: "GET",
      }),
      providesTags: [ApiTags.EdlinkIntegrations],
      transformResponse: (result: IApiResponse<IEdlinkOnboarding | null>) => result.data ?? null,
    }),
    completeOnboarding: builder.mutation<
      IEdlinkIntegration,
      { districtId: string; id: string }
    >({
      extraOptions: onboardingExtraOptions,
      query: ({ districtId, id }) => ({
        url: `/districts/${districtId}/edlink/onboardings/${id}/complete`,
        method: "POST",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations]),
      transformResponse: (result: IApiResponse<IEdlinkIntegration>) => result.data,
    }),
    discardOnboarding: builder.mutation<void, { districtId: string; id: string }>({
      extraOptions: onboardingExtraOptions,
      query: ({ districtId, id }) => ({
        url: `/districts/${districtId}/edlink/onboardings/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations]),
    }),
    getSettings: builder.query<IEdlinkSettings, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/edlink/settings`,
        method: "GET",
      }),
      providesTags: [ApiTags.EdlinkSettings],
      transformResponse: (result: IApiResponse<IEdlinkSettings>) => result.data,
    }),
    updateSettings: builder.mutation<
      IUpdateEdlinkSettingsResponse,
      IUpdateEdlinkSettingsRequest & { districtId: string }
    >({
      query: ({
        districtId,
        edlinkSyncEnabled,
        includeHistorical,
        monthlySnapshotDay,
        fullSyncMonth,
        fullSyncDay,
      }) => ({
        url: `/districts/${districtId}/edlink/settings`,
        method: "PATCH",
        body: {
          edlinkSyncEnabled,
          includeHistorical,
          monthlySnapshotDay,
          fullSyncMonth,
          fullSyncDay,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkSettings, ApiTags.EdlinkIntegrations]),
      transformResponse: (result: IApiResponse<IUpdateEdlinkSettingsResponse>) => result.data,
    }),
    enqueueSync: builder.mutation<
      IEnqueueEdlinkSyncResponse,
      IEnqueueEdlinkSyncRequest & { districtId: string }
    >({
      query: ({ districtId, kind }) => ({
        url: `/districts/${districtId}/edlink/sync`,
        method: "POST",
        body: { kind },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations]),
      transformResponse: (result: IApiResponse<IEnqueueEdlinkSyncResponse>) => result.data,
    }),
    listJobs: builder.query<IEdlinkSyncJob[], { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/edlink/jobs`,
        method: "GET",
      }),
      providesTags: [ApiTags.EdlinkIntegrations],
      transformResponse: (result: IApiResponse<IEdlinkSyncJob[]>) => result.data ?? [],
    }),
    listJobErrors: builder.query<
      IEdlinkSyncJobErrorsListResponse,
      { districtId: string; jobId: string; page?: number; pageSize?: number }
    >({
      query: ({
        districtId, jobId, page = 1, pageSize = 20,
      }) => ({
        url: `/districts/${districtId}/edlink/jobs/${jobId}/errors`,
        method: "GET",
        params: { page, pageSize },
      }),
      transformResponse: (result: IApiResponse<IEdlinkSyncJobErrorsListResponse>) => result.data,
    }),
    listCustomFieldMappings: builder.query<
      IListCustomFieldMappingsResponse,
      { districtId: string; integrationId: string }
    >({
      query: ({ districtId, integrationId }) => ({
        url: `/districts/${districtId}/edlink/integrations/${integrationId}/custom-field-mappings`,
        method: "GET",
      }),
      providesTags: [ApiTags.EdlinkCustomFieldMappings],
      transformResponse: (result: IApiResponse<IListCustomFieldMappingsResponse>) =>
        result.data ?? { items: [], maxSourcesPerTarget: 1 },
    }),
    refreshCustomPropertyKeys: builder.mutation<
      IRefreshCustomPropertyKeysResponse,
      { districtId: string; integrationId: string }
    >({
      query: ({ districtId, integrationId }) => ({
        url: `/districts/${districtId}/edlink/integrations/${integrationId}/custom-property-keys/refresh`,
        method: "POST",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkCustomFieldMappings]),
      transformResponse: (result: IApiResponse<IRefreshCustomPropertyKeysResponse>) => result.data,
    }),
    saveCustomFieldMappings: builder.mutation<
      IListCustomFieldMappingsResponse,
      { districtId: string; integrationId: string; items: ICustomFieldMappingChange[] }
    >({
      query: ({ districtId, integrationId, items }) => ({
        url: `/districts/${districtId}/edlink/integrations/${integrationId}/custom-field-mappings`,
        method: "PUT",
        body: { items },
      }),
      transformResponse: (result: IApiResponse<IListCustomFieldMappingsResponse>) => result.data,
      // The response is the saved list; write it to the cache so the page shows it without a refetch.
      async onQueryStarted({ districtId, integrationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            edlinkApi.util.upsertQueryData("listCustomFieldMappings", { districtId, integrationId }, data),
          );
        } catch {
          // The caller shows the error; the cached list stays as it was.
        }
      },
    }),
    enqueueCustomFieldBackfill: builder.mutation<
      IEnqueueCustomFieldBackfillResponse,
      { districtId: string; integrationId: string }
    >({
      query: ({ districtId, integrationId }) => ({
        url: `/districts/${districtId}/edlink/integrations/${integrationId}/custom-field-mappings/backfill`,
        method: "POST",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.EdlinkIntegrations, ApiTags.EdlinkCustomFieldMappings]),
      transformResponse: (result: IApiResponse<IEnqueueCustomFieldBackfillResponse>) => result.data,
    }),
    suggestCustomFieldMappings: builder.mutation<
      ISuggestCustomFieldMappingsResponse,
      { districtId: string; integrationId: string; items: ICustomFieldMappingChange[] }
    >({
      query: ({ districtId, integrationId, items }) => ({
        url: `/districts/${districtId}/edlink/integrations/${integrationId}/custom-field-mappings/suggest`,
        method: "POST",
        body: { items },
      }),
      transformResponse: (result: IApiResponse<ISuggestCustomFieldMappingsResponse>) => result.data,
    }),
  }),
});

export const {
  useListIntegrationsQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useListIntegrationSchoolOptionsQuery,
  useDeleteIntegrationMutation,
  useStartOnboardingMutation,
  useGetPendingOnboardingQuery,
  useCompleteOnboardingMutation,
  useDiscardOnboardingMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useEnqueueSyncMutation,
  useListJobsQuery,
  useListJobErrorsQuery,
  useListCustomFieldMappingsQuery,
  useRefreshCustomPropertyKeysMutation,
  useSaveCustomFieldMappingsMutation,
  useEnqueueCustomFieldBackfillMutation,
  useSuggestCustomFieldMappingsMutation,
} = edlinkApi;
export default edlinkApi;
